import { useEffect, useMemo, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useProjectIdentifiers } from "store/hooks/useProjectIdentifiers";
import { scriptAiPipelineLockKeys } from "queries";
import {
  isScriptAiHubFeatureRunning,
  isScriptHubFeatureStatusActive,
  SCRIPT_AI_HUB_FEATURES,
  type ScriptAiHubFeature,
} from "utils/scriptAiPipelineRunning";
import { checkProjectFeatureStatus, mapProjectFeatureStatusesToFeatureStatus } from "utils";
import { resolveRunAiHubPipelineVisibility } from "utils/runAiHubPipelineVisibility";
import type { ProjectState } from "hooks/useProjects/useProjects.types";

/** When the optimistic lock outlives server status, force a reconcile refetch. */
export const SCRIPT_AI_LOCK_VERIFY_MS = 30_000;

type UseScriptAiRunStatusPollingOptions = {
  projectId: string;
  documentId: string;
  hasValidIds: boolean;
  watchFeatures?: readonly ScriptAiHubFeature[];
  isAnyMutationPending?: boolean;
  forcePolling?: boolean;
};

function getFeatureEnabled(project: ProjectState | null, feature: ScriptAiHubFeature): boolean {
  const hub = resolveRunAiHubPipelineVisibility(project);
  if (feature === "script_breakdown") return hub.hasScriptBreakdown;
  if (feature === "character_breakdown") return hub.hasCharacterBreakdown;
  return hub.hasCoverage;
}

export function shouldPollScriptAiFeature(
  project: ProjectState | null | undefined,
  projectId: string | number,
  feature: ScriptAiHubFeature
): boolean {
  if (!project) return false;

  if (isScriptAiHubFeatureRunning(project.featureStatuses, projectId, feature)) {
    return true;
  }

  if (!getFeatureEnabled(project, feature)) return false;

  const mapped = mapProjectFeatureStatusesToFeatureStatus(project.featureStatuses, projectId);
  if (!mapped.length) return true;

  const ready = checkProjectFeatureStatus(mapped, feature);
  if (!ready) return true;

  return false;
}

export function useScriptAiRunStatusPolling({
  projectId,
  documentId,
  hasValidIds,
  watchFeatures = SCRIPT_AI_HUB_FEATURES,
  isAnyMutationPending = false,
  forcePolling = false,
}: UseScriptAiRunStatusPollingOptions) {
  const queryClient = useQueryClient();
  const lockSetAtRef = useRef<number | null>(null);

  const documentScope = hasValidIds ? documentId : undefined;
  const { project: initialProject } = useProjectIdentifiers(false, documentScope);

  const lockKey = scriptAiPipelineLockKeys.document(projectId, documentId);
  const { data: scriptAiPipelineLock = false } = useQuery<boolean>({
    queryKey: lockKey,
    queryFn: () => false,
    initialData: false,
    staleTime: Number.POSITIVE_INFINITY,
    enabled: false,
  });

  useEffect(() => {
    if (scriptAiPipelineLock) {
      if (lockSetAtRef.current === null) {
        lockSetAtRef.current = Date.now();
      }
      return;
    }
    lockSetAtRef.current = null;
  }, [scriptAiPipelineLock]);

  const featureNeedsPoll = useMemo(() => {
    if (!initialProject) return false;
    return watchFeatures.some((feature) =>
      shouldPollScriptAiFeature(initialProject, projectId, feature)
    );
  }, [initialProject, projectId, watchFeatures]);

  const shouldPoll = Boolean(
    hasValidIds &&
    (forcePolling || featureNeedsPoll || isAnyMutationPending || scriptAiPipelineLock)
  );

  const { project: polledProject, refetchProject } = useProjectIdentifiers(
    shouldPoll,
    documentScope
  );
  const projectForAi = polledProject ?? initialProject;

  const scopedFeatureNeedsPoll = useMemo(() => {
    if (!projectForAi) return false;
    return watchFeatures.some((feature) =>
      shouldPollScriptAiFeature(projectForAi, projectId, feature)
    );
  }, [projectForAi, projectId, watchFeatures]);

  const clearScriptAiPipelineLock = () => {
    queryClient.setQueryData(lockKey, false);
  };

  useEffect(() => {
    if (!scriptAiPipelineLock) return;

    const hubStillRunning = isScriptHubFeatureStatusActive(
      projectForAi?.featureStatuses,
      projectId
    );
    if (!hubStillRunning && !isAnyMutationPending) {
      clearScriptAiPipelineLock();
    }
  }, [
    isAnyMutationPending,
    projectForAi?.featureStatuses,
    projectId,
    scriptAiPipelineLock,
    queryClient,
    lockKey,
  ]);

  // Tab return refresh while polling lives in getUseProject (useRefetchOnPageVisible).

  useEffect(() => {
    if (!scriptAiPipelineLock || !hasValidIds) return;
    if (scopedFeatureNeedsPoll || isAnyMutationPending) return;

    const lockAgeMs = lockSetAtRef.current === null ? 0 : Date.now() - lockSetAtRef.current;
    if (lockAgeMs < SCRIPT_AI_LOCK_VERIFY_MS) return;

    void refetchProject();
  }, [
    hasValidIds,
    isAnyMutationPending,
    refetchProject,
    scopedFeatureNeedsPoll,
    scriptAiPipelineLock,
  ]);

  return {
    project: projectForAi,
    shouldPoll,
    scriptAiPipelineLock,
    isPipelineActive: scopedFeatureNeedsPoll || isAnyMutationPending || scriptAiPipelineLock,
    refetchProject,
    clearScriptAiPipelineLock,
  };
}
